import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Download, Copy, Share2, Sparkles, Loader2 } from "lucide-react";

// Static placeholder share card image — swap for any hosted image URL
// or a local asset import if you prefer
const STATIC_SHARE_CARD_URL =
  "https://placehold.co/1200x630/0A1D1A/C6A87C?text=vmeetio+Breakthrough+Card";

export default function ShareCardDialog({
  breakthroughId,
  breakthroughText,
  existingUrl,
  onClose,
}) {
  const [url, setUrl] = useState(existingUrl || null);
  const [loading, setLoading] = useState(!existingUrl);
  const [error, setError] = useState(null);

  const fullUrl = (path) => {
    if (!path) return "";
    if (/^https?:/i.test(path)) return path;
    const base = window.location.origin;
    return `${base}${path}`;
  };

  useEffect(() => {
    if (existingUrl) return;

    let cancelled = false;

    const simulateGeneration = async () => {
      // Simulate the ~10-20 second generation delay (shortened for demo)
      await new Promise((resolve) => setTimeout(resolve, 1500));

      if (cancelled) return;

      try {
        // Static success — use placeholder card image
        setUrl(STATIC_SHARE_CARD_URL);
      } catch (err) {
        if (cancelled) return;
        setError("Couldn't generate share card. Please retry.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    simulateGeneration();

    return () => {
      cancelled = true;
    };
  }, [breakthroughId, existingUrl]);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl(url));
      toast.success("Link copied. Paste it anywhere.");
    } catch {
      toast.error("Copy failed — long-press the image instead.");
    }
  };

  const nativeShare = async () => {
    const shareUrl = fullUrl(url);
    if (navigator.share) {
      try {
        await navigator.share({
          title: "My breakthrough at vmeetio",
          text: breakthroughText,
          url: shareUrl,
        });
      } catch {
        /* user dismissed */
      }
    } else {
      copyUrl();
    }
  };

  return (
    <div
      data-testid="share-card-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-[#061412]/85 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="glass rounded-2xl p-7 max-w-2xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="overline text-[#C6A87C] mb-2 flex items-center gap-2">
              <Sparkles size={12} /> Auto-designed by vmeetio
            </p>
            <h3 className="font-display text-2xl tracking-tight">
              Your shareable breakthrough card
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-white/50 hover:text-white p-1"
            data-testid="share-card-close"
          >
            <X size={20} />
          </button>
        </div>

        {loading && (
          <div
            className="aspect-[1200/630] w-full rounded-xl bg-white/[0.04] border border-white/[0.08] flex flex-col items-center justify-center gap-3"
            data-testid="share-card-loading"
          >
            <Loader2 className="animate-spin text-[#C6A87C]" size={28} />
            <p className="text-sm text-white/55">
              Generating your card with Nano Banana — ~10–20 seconds…
            </p>
          </div>
        )}

        {error && (
          <div
            className="rounded-xl bg-red-500/[0.08] border border-red-500/30 p-5"
            data-testid="share-card-error"
          >
            <p className="text-sm text-red-200">{error}</p>
          </div>
        )}

        {url && !loading && (
          <>
            <div className="rounded-xl overflow-hidden border border-white/[0.08] mb-5">
              <img
                src={fullUrl(url)}
                alt="Your breakthrough share card"
                className="w-full block"
                data-testid="share-card-image"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <a
                href={fullUrl(url)}
                download="vmeetio-breakthrough.png"
                data-testid="share-card-download"
                className="btn-gold text-sm"
              >
                <Download size={14} /> Download
              </a>
              <button
                onClick={copyUrl}
                data-testid="share-card-copy"
                className="btn-ghost text-sm"
              >
                <Copy size={14} /> Copy link
              </button>
              <button
                onClick={nativeShare}
                data-testid="share-card-native-share"
                className="btn-ghost text-sm"
              >
                <Share2 size={14} /> Share
              </button>
            </div>
            <p className="text-xs text-white/40 mt-4">
              Post it on LinkedIn, Twitter, or your team Slack. The card is
              yours — it's stored on the vmeetio CDN and won't change.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
